<template>
  <div class="editor-page">
    <div v-show="!noHeader" class="editor-header">
      <slot name="editor-header" :titleLabel="titleLabel">
        <div class="editor-title">{{ titleLabel }}</div>
        <slot name="title-left"></slot>
      </slot>
      <slot name="extra-header"></slot>
    </div>
    <div class="form-container" :style="{ width: formWidth }">
      <div class="form-header">
        <slot name="form-header"></slot>
      </div>
      <el-form
        ref="form"
        v-rect-loading="formLoading"
        :class="[requiredAsteriskPosition === 'after' ? 'asterisk-after' : 'asterisk-before', labelPosition && `label-position-${labelPosition}`]"
        :label-position="labelPosition"
        :inline="inlineForm"
        :label-width="labelWidth"
        :model="model"
        :disabled="disabled"
        :hide-required-asterisk="hideRequiredAsterisk"
      >
        <template v-for="(line, lineName) in formLines">
          <el-form-item
            v-if="!line.hidden"
            :key="lineName"
            :label="selectFuncLabelI18n([line, lineName], line)"
            :class="line.showRequiredAsterisk && 'is-required'"
            :prop="line.dataKey || lineName"
            :rules="line.rules"
          >
            <slot :name="line.slotName || lineName" :line="line" :lineName="lineName" :dataKey="line.dataKey || lineName" :placeholder="selectFuncLabelI18n([line, lineName], line, 'placeholder')">
              <el-input v-model="model[line.dataKey || lineName]" :placeholder="selectFuncLabelI18n([line, lineName], line, 'placeholder')"></el-input>
            </slot>
          </el-form-item>
        </template>
      </el-form>
      <slot name="form-bottom"></slot>
      <div class="bottom-btn-group">
        <slot name="btnGroups">
          <el-button class="cancel-btn" @click="handleCancel">{{ cancelBtnLabel }}</el-button>
          <el-button class="save-btn" @click="handleSave">{{ saveBtnLabel }}</el-button>
        </slot>
      </div>
    </div>
    <slot name="page-bottom"></slot>
  </div>
</template>

<script>
import { selectLabelI18n, selectFuncLabelI18n } from '@/i18n'

export default {
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    noHeader: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      required: false
    },
    titleI18nKey: {
      type: String,
      required: false
    },
    formWidth: {
      type: String,
      default: '606px'
    },
    labelWidth: {
      type: String,
      default: 'auto'
    },
    model: {
      type: Object,
      required: true
    },
    formLines: {
      type: Object,
      required: true
    },
    inlineForm: {
      type: Boolean,
      default: false
    },
    labelPosition: {
      type: String,
      default: 'left'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    requiredAsteriskPosition: {
      type: String,
      default: 'after'
    },
    hideRequiredAsterisk: {
      type: Boolean,
      default: false
    },
    cancelBtn: {
      type: String,
      required: false
    },
    cancelBtnI18nKey: {
      type: String,
      required: false
    },
    saveBtn: {
      type: String,
      required: false
    },
    saveBtnI18nKey: {
      type: String,
      required: false
    }
  },
  data () {
    return {
      selectLabelI18n,
      selectFuncLabelI18n
    }
  },
  computed: {
    titleLabel () {
      return selectLabelI18n(this, 'title')
    },
    cancelBtnLabel () {
      return selectLabelI18n(this, 'cancelBtn', 'common.btn.cancel')
    },
    saveBtnLabel () {
      return selectLabelI18n(this, 'saveBtn', 'common.btn.save')
    },
    form () {
      const form = this.$refs.form
      form.validate = this.validate
      return form
    },
    formLoading: {
      get () {
        return this.loading
      },
      set (val) {
        this.$emit('update:loading', val)
      }
    }
  },
  created () {

  },
  methods: {
    validate (callBack) {
      const form = this.$refs.form
      if (!callBack) {
        return form.validate().finally(() => {
          this.$nextTick(() => {
            document.querySelector('.el-form-item.is-error').scrollIntoView({ block: 'center', behavior: 'smooth' })
          })
        })
      } else {
        form.validate((isSuccess, failObject) => {
          if (!isSuccess) {
            this.$nextTick(() => {
              document.querySelector('.el-form-item.is-error').scrollIntoView({ block: 'center', behavior: 'smooth' })
            })
          }
          callBack(isSuccess, failObject)
        })
      }
    },
    handleCancel () {
      if (this.formLoading) {
        return
      }
      this.$emit('cancel')
    },
    handleSave () {
      if (this.formLoading) {
        return
      }
      this.formLoading = true
      let result = false
      this.validate((isSuccess) => {
        result = isSuccess
      })
      if (!result) {
        this.formLoading = false
        return
      }
      this.$emit('save', () => {
        this.formLoading = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.editor-page {
  .editor-header {
    display: flex;
    border-bottom: 1px solid #e4e7ed;

    .editor-title {
      padding-top: 4px;
      padding-bottom: 18px;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }

  .form-container {
    margin: 30px auto 0;

    .el-form {
      &.asterisk-after {
        ::v-deep {
          .el-form-item {
            &:not(.is-no-asterisk) {
              .el-form-item__label {

                &::after {
                  content: '*';
                  margin: 9px 0 0 3px;
                  color: #f56c6c;
                  line-height: 1;
                  visibility: hidden;
                }
              }
            }

            &.is-required:not(.is-no-asterisk) {
              .el-form-item__label {
                &::before {
                  display: none;
                }

                &::after {
                  visibility: unset;
                }
              }
            }
          }
        }
      }

      .el-form-item {
        ::v-deep {
          .el-form-item__label {
            padding: 0 40px 0 0;
            color: #606266;
            line-height: 32px;
          }

          .el-form-item__content {
            line-height: 32px;
          }

          .el-input__inner {
            height: 32px;
            line-height: 32px;
            border-radius: 2px;
          }

          .el-select {
            width: 100%;
          }
        }
      }
    }
  }

  .bottom-btn-group {
    display: flex;
    margin-top: 40px;
    justify-content: flex-end;

    .el-button {
      padding: 8px 28px;
      border-radius: 2px;
      line-height: 22px;
      font-size: 16px;
      font-weight: 500;
      color: #0096cc;

      &:last-child {
        margin-left: 24px;
        margin-right: 0;
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;
      }
    }
  }
}
</style>
